import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { api } from '../../lib/api';
import { ArrowLeft } from 'lucide-react';

interface ExchangeCar {
  id: number;
  personOneCarBrand: string;
  personOneCarModel: string;
  personOneCarYear: number;
  personOneCarPlateNo: string;
  personOneCarChassisNo?: string | null;
  personOneCarColor: string;
  personOneName: string;
  personOneIdNo: string;
  personOneAddress: string | null;
  personOneContact: string;
  personTwoCarBrand: string;
  personTwoCarModel: string;
  personTwoCarYear: number;
  personTwoCarPlateNo: string;
  personTwoCarChassisNo?: string | null;
  personTwoCarColor: string;
  personTwoName: string;
  personTwoIdNo: string;
  personTwoAddress: string | null;
  personTwoContact: string;
  amountOwed: number;
  paidAmount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function ExchangeCarDetail() {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exchangeCar, setExchangeCar] = useState<ExchangeCar | null>(null);

  useEffect(() => {
    if (id) {
      fetchExchangeCarDetails();
    }
  }, [id]);

  const fetchExchangeCarDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await api.get(`/api/exchange-cars/${id}`);
      if (data && typeof data === 'object' && 'id' in data && !('exchangeCars' in data)) {
        setExchangeCar(data as ExchangeCar);
      } else {
        console.error('Failed to fetch exchange car details');
      }
    } catch (error) {
      console.error('Error fetching exchange car details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatPrice = (amount: number) => {
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!exchangeCar) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('exchangeCarDetail.notFound')}</h1>
          <Button onClick={() => navigate('/exchange-cars')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('exchangeCarDetail.backToHistory')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/exchange-cars')} className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium flex items-center">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('exchangeCarDetail.backToHistory')}
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('exchangeCarDetail.title')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {t('exchangeCarDetail.exchangeId')}: #{exchangeCar.id}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Exchange Date and Financial Info */}
        <div className="mb-6">
          <Card className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <Label className="text-sm font-medium text-gray-600">{t('exchangeCarDetail.exchangeDate')}</Label>
                <p className="text-lg">{formatDate(exchangeCar.createdAt)}</p>
              </div>
              {(exchangeCar.amountOwed > 0 || exchangeCar.paidAmount > 0) && (
                <div className="text-right space-y-2">
                  {exchangeCar.amountOwed > 0 && (
                    <div>
                      <Label className="text-sm font-medium text-gray-600">{t('exchangeCarDetail.amountOwed')}</Label>
                      <p className="text-lg font-semibold text-red-600">{formatPrice(exchangeCar.amountOwed)}</p>
                    </div>
                  )}
                  {exchangeCar.paidAmount > 0 && (
                    <div>
                      <Label className="text-sm font-medium text-gray-600">{t('exchangeCarDetail.paidAmount')}</Label>
                      <p className="text-lg font-semibold text-green-600">{formatPrice(exchangeCar.paidAmount)}</p>
                    </div>
                  )}
                  {exchangeCar.amountOwed > 0 && (
                    <div>
                      <Label className="text-sm font-medium text-gray-600">{t('exchangeCarDetail.remainingBalance')}</Label>
                      <p className="text-lg font-semibold text-blue-600">{formatPrice(Math.max(0, exchangeCar.amountOwed - (exchangeCar.paidAmount || 0)))}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Person One Section */}
          <Card className="p-6 border-2 border-cyan-200 bg-cyan-50/50">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('exchangeCarDetail.personOne')}</h2>
            
            {/* Person One Car Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('exchangeCarDetail.carDetails')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.brand')}:</span>
                  <span>{exchangeCar.personOneCarBrand}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.model')}:</span>
                  <span>{exchangeCar.personOneCarModel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.year')}:</span>
                  <span>{exchangeCar.personOneCarYear}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.color')}:</span>
                  <span>{exchangeCar.personOneCarColor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.plateNumber')}:</span>
                  <span className="font-mono">{exchangeCar.personOneCarPlateNo}</span>
                </div>
                {exchangeCar.personOneCarChassisNo && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">{t('exchangeCarDetail.chassisNumber')}:</span>
                    <span className="font-mono">{exchangeCar.personOneCarChassisNo}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Person One Personal Details */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('exchangeCarDetail.personalDetails')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.name')}:</span>
                  <span>{exchangeCar.personOneName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.idNo')}:</span>
                  <span className="font-mono">{exchangeCar.personOneIdNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.contact')}:</span>
                  <span>{exchangeCar.personOneContact}</span>
                </div>
                {exchangeCar.personOneAddress && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">{t('exchangeCarDetail.address')}:</span>
                    <span>{exchangeCar.personOneAddress}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Person Two Section */}
          <Card className="p-6 border-2 border-emerald-200 bg-emerald-50/50">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('exchangeCarDetail.personTwo')}</h2>
            
            {/* Person Two Car Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('exchangeCarDetail.carDetails')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.brand')}:</span>
                  <span>{exchangeCar.personTwoCarBrand}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.model')}:</span>
                  <span>{exchangeCar.personTwoCarModel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.year')}:</span>
                  <span>{exchangeCar.personTwoCarYear}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.color')}:</span>
                  <span>{exchangeCar.personTwoCarColor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.plateNumber')}:</span>
                  <span className="font-mono">{exchangeCar.personTwoCarPlateNo}</span>
                </div>
                {exchangeCar.personTwoCarChassisNo && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">{t('exchangeCarDetail.chassisNumber')}:</span>
                    <span className="font-mono">{exchangeCar.personTwoCarChassisNo}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Person Two Personal Details */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('exchangeCarDetail.personalDetails')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.name')}:</span>
                  <span>{exchangeCar.personTwoName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.idNo')}:</span>
                  <span className="font-mono">{exchangeCar.personTwoIdNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">{t('exchangeCarDetail.contact')}:</span>
                  <span>{exchangeCar.personTwoContact}</span>
                </div>
                {exchangeCar.personTwoAddress && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">{t('exchangeCarDetail.address')}:</span>
                    <span>{exchangeCar.personTwoAddress}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Notes */}
        {exchangeCar.notes && (
          <div className="mt-8">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">{t('exchangeCarDetail.notes')}</h3>
              <p className="text-sm text-gray-700">{exchangeCar.notes}</p>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

